import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation } from "react-router-dom";
import CardRating from "../../product/CardRating.tsx";
import BarSpinner from "../../Loader/BarSpinner.tsx";
import { Product } from "../../../models/product.ts";
import { SizeOption } from "../../../models/size-option.ts";
import { ProductType } from "../../../models/product-type.ts";
import { CategoryType } from "../../../models/category-type.ts";
import { getProducts } from "../../../Services/product.service.ts";
import Filter, { FilterDismiss } from "../Filters/Filter.tsx";

type ActiveFilters = {
  categories: CategoryType[];
  productTypes: ProductType[];
  sizes: SizeOption[];
  colors: string[];
  designs: string[];
  fabrics: string[];
  priceRange: { min: number; max: number };
};

const emptyFilters: ActiveFilters = {
  categories: [],
  productTypes: [],
  sizes: [],
  colors: [],
  designs: [],
  fabrics: [],
  priceRange: { min: 0, max: 50000 },
};

const saleAmount = (product: Product) =>
  typeof product.sale === "number" ? product.sale : 0;

const ShopDetails = () => {
  const location = useLocation();
  const [productsPerPage] = useState(12);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetFilters, setResetFilters] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hideFilters, setHideFilters] = useState(false);
  const [columns, setColumns] = useState(4);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 900px)").matches,
  );
  const [dismiss, setDismiss] = useState<FilterDismiss | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(emptyFilters);
  const queryParams = new URLSearchParams(location.search);
  const initialCategory = queryParams.get("category") || "";
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [originalProducts, setOriginalProducts] = useState<Product[]>([]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const response = await getProducts();
      if (response.success && Array.isArray(response.data)) {
        setOriginalProducts(response.data);
        setFilteredProducts(response.data);
      } else {
        setOriginalProducts([]);
        toast.error("Failed to fetch products");
      }
      setLoading(false);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("az-fopen", filtersOpen);
    return () => document.body.classList.remove("az-fopen");
  }, [filtersOpen]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const sync = () => setIsMobile(media.matches);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    filterProductsByFilters(originalProducts, activeFilters);
    // Re-apply the current filters once the catalog finishes loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalProducts]);

  const handleFilterChange = (nextFilters: ActiveFilters) => {
    setActiveFilters(nextFilters);
    filterProductsByFilters(originalProducts, nextFilters);
    setCurrentPage(1);
  };

  const filterProductsByFilters = (
    source: Product[],
    nextFilters: ActiveFilters,
  ) => {
    let next = [...source];
    const untouched =
      nextFilters.productTypes.length === 0 &&
      nextFilters.categories.length === 0 &&
      nextFilters.sizes.length === 0 &&
      nextFilters.colors.length === 0 &&
      nextFilters.priceRange.min === 0 &&
      nextFilters.priceRange.max === 50000 &&
      nextFilters.designs.length === 0 &&
      nextFilters.fabrics.length === 0;
    if (untouched) {
      setFilteredProducts(source);
      return;
    }
    if (nextFilters.categories.length > 0) {
      next = next.filter((product) =>
        nextFilters.categories.includes(product.category.name),
      );
    }
    if (nextFilters.productTypes.length > 0) {
      next = next.filter((product) =>
        nextFilters.productTypes.includes(product.type as ProductType),
      );
    }
    if (nextFilters.sizes.length > 0) {
      next = next.filter((product) =>
        product.stockOptions.some((option) =>
          nextFilters.sizes.includes(option.size as SizeOption),
        ),
      );
    }
    if (nextFilters.colors.length > 0) {
      next = next.filter((product) =>
        product.stockOptions.some((option) =>
          nextFilters.colors.includes(option.color.hex),
        ),
      );
    }
    if (
      nextFilters.priceRange.min !== 0 ||
      nextFilters.priceRange.max !== 50000
    ) {
      next = next.filter(
        (product) =>
          product.price >= nextFilters.priceRange.min &&
          product.price <= nextFilters.priceRange.max,
      );
    }
    if (nextFilters.designs.length > 0) {
      next = next.filter((product) =>
        nextFilters.designs.includes(product.design as string),
      );
    }
    if (nextFilters.fabrics.length > 0) {
      next = next.filter((product) =>
        nextFilters.fabrics.includes(product.fabric as string),
      );
    }
    setFilteredProducts(next);
  };

  const showAllProducts = () => {
    setFilteredProducts(originalProducts);
    setCurrentPage(1);
    setResetFilters((prev) => !prev);
    scrollToTop();
  };

  const handleSorting = (sortValue: string) => {
    const sorted = [...filteredProducts];
    switch (sortValue) {
      case "a-z":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "z-a":
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "lowToHigh":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "highToLow":
        sorted.sort((a, b) => b.price - a.price);
        break;
      default:
        setFilteredProducts([...originalProducts]);
        filterProductsByFilters(originalProducts, activeFilters);
        return;
    }
    setFilteredProducts(sorted);
  };

  const toggleFilters = () => {
    if (window.matchMedia("(max-width: 900px)").matches) {
      setFiltersOpen(true);
    } else {
      setHideFilters((open) => !open);
    }
  };

  const chips = [
    ...activeFilters.categories.map((value) => ({
      group: "categories",
      value,
      label: value,
    })),
    ...activeFilters.productTypes.map((value) => ({
      group: "productTypes",
      value,
      label: value,
    })),
    ...activeFilters.designs.map((value) => ({
      group: "designs",
      value,
      label: value,
    })),
    ...activeFilters.fabrics.map((value) => ({
      group: "fabrics",
      value,
      label: value,
    })),
    ...activeFilters.sizes.map((value) => ({
      group: "sizes",
      value,
      label: value,
    })),
    ...activeFilters.colors.map((value) => ({
      group: "colors",
      value,
      label: "Colour",
    })),
  ];

  const removeChip = (group: string, value: string) => {
    setDismiss({ group, value, nonce: Date.now() });
  };

  return (
    <div className="az">
      <main className="az-wrap">
        <div className="crumbs">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>Shop All</span>
        </div>
        <div className="shead">
          <h2 className="serif">Shop All</h2>
          <p>
            Every piece in the collection — suits, shawl sets and ready-to-wear
            kurtas.
          </p>
        </div>
        <div className="toolbar">
          <button className="fbtn" onClick={toggleFilters}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <path d="M2 4h12M4.5 8h7M7 12h2" />
            </svg>
            <span>
              {isMobile ? "Filter" : hideFilters ? "Show filters" : "Hide filters"}
            </span>
            <span className={`badge${chips.length ? " show" : ""}`}>
              {chips.length || ""}
            </span>
          </button>
          <span className="count">
            {filteredProducts.length} product
            {filteredProducts.length === 1 ? "" : "s"}
          </span>
          <div className="tright">
            <div className="dens" role="group" aria-label="Grid size">
              <button
                className={columns === 2 ? "on" : ""}
                aria-label="2 per row"
                onClick={() => setColumns(2)}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="1" width="6" height="14" rx="1" />
                  <rect x="9" y="1" width="6" height="14" rx="1" />
                </svg>
              </button>
              <button
                className={columns === 3 ? "on" : ""}
                aria-label="3 per row"
                onClick={() => setColumns(3)}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <rect x="0.5" y="1" width="4" height="14" rx="1" />
                  <rect x="6" y="1" width="4" height="14" rx="1" />
                  <rect x="11.5" y="1" width="4" height="14" rx="1" />
                </svg>
              </button>
              <button
                className={columns === 4 ? "on" : ""}
                aria-label="4 per row"
                onClick={() => setColumns(4)}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <rect x="0" y="1" width="3" height="14" rx="0.8" />
                  <rect x="4.3" y="1" width="3" height="14" rx="0.8" />
                  <rect x="8.6" y="1" width="3" height="14" rx="0.8" />
                  <rect x="13" y="1" width="3" height="14" rx="0.8" />
                </svg>
              </button>
            </div>
            <label className="sort">
              <span>Sort by</span>
              <select
                defaultValue="default"
                onChange={(event) => handleSorting(event.target.value)}
              >
                <option value="default">Featured</option>
                <option value="lowToHigh">Price, low to high</option>
                <option value="highToLow">Price, high to low</option>
                <option value="a-z">Name, A–Z</option>
                <option value="z-a">Name, Z–A</option>
              </select>
            </label>
          </div>
        </div>
        <div className="chips">
          {chips.map((chip) => (
            <button
              className="chip"
              key={`${chip.group}-${chip.value}`}
              onClick={() => removeChip(chip.group, chip.value)}
            >
              {chip.group === "colors" && (
                <span
                  className="d"
                  style={{
                    background: chip.value,
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                />
              )}
              {chip.label}
              <i aria-hidden="true">×</i>
            </button>
          ))}
          {chips.length > 0 && (
            <button className="chip clear" onClick={showAllProducts}>
              Clear all
            </button>
          )}
        </div>
        {loading ? (
          <div className="loader-wrap">
            <BarSpinner />
          </div>
        ) : (
          <div className={`layout${hideFilters ? " nof" : ""}`}>
            <aside className="filters" aria-label="Filters">
              <div className="fhead">
                <b>Filter</b>
                <button aria-label="Close filters" onClick={() => setFiltersOpen(false)}>
                  ×
                </button>
              </div>
              <div className="fbody">
                <Filter
                  onFilterChange={handleFilterChange}
                  resetFilters={resetFilters}
                  initialCategory={initialCategory}
                  dismiss={dismiss}
                />
              </div>
              <div className="ffoot">
                <button className="ghost" onClick={showAllProducts}>
                  Clear all
                </button>
                <button className="solid" onClick={() => setFiltersOpen(false)}>
                  Show results
                </button>
              </div>
            </aside>
            <section>
              {currentProducts.length === 0 ? (
                <div className="empty">
                  <h3>No products found</h3>
                  <p>Try adjusting your filters or browse all products</p>
                  <button className="btn" onClick={showAllProducts}>
                    View all products
                  </button>
                </div>
              ) : (
                <div className="sgrid" style={{ ["--cols" as string]: columns }}>
                  {currentProducts.map((product) => {
                    const sale = saleAmount(product);
                    const finalPrice =
                      sale > 0
                        ? Math.round(product.price * (1 - sale / 100))
                        : product.price;
                    return (
                      <Link
                        className="card"
                        to={`/product/${product.id}`}
                        state={{ product }}
                        key={product.id}
                        onClick={scrollToTop}
                      >
                        <div className="ph">
                          {product.isOutOfStock && (
                            <span className="tag">Sold Out</span>
                          )}
                          {!product.isOutOfStock && sale > 0 && (
                            <span className="tag">-{sale}%</span>
                          )}
                          <img src={product.images.front} alt={product.name} />
                          {product.images.back && (
                            <img className="h" src={product.images.back} alt="" />
                          )}
                        </div>
                        <h3>{product.name}</h3>
                        <div className="p">
                          Rs {finalPrice.toLocaleString()}
                          {sale > 0 && <s>Rs {product.price.toLocaleString()}</s>}
                          <CardRating
                            average={product.ratingAverage}
                            count={product.ratingCount}
                          />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
              {totalPages > 1 && (
                <div className="pager">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => {
                      setCurrentPage((page) => page - 1);
                      scrollToTop();
                    }}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => (
                    <button
                      key={index + 1}
                      className={currentPage === index + 1 ? "on" : ""}
                      onClick={() => {
                        setCurrentPage(index + 1);
                        scrollToTop();
                      }}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => {
                      setCurrentPage((page) => page + 1);
                      scrollToTop();
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
      <div className="scrim" onClick={() => setFiltersOpen(false)} />
    </div>
  );
};

export default ShopDetails;
